import React, { useState } from 'react';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { authAPI } from '../services/api';

const ForgotPassword: React.FC = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      await authAPI.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      setError('En feil oppstod. Vennligst prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center bg-gradient-to-br from-[#667eea] to-[#764ba2]">
      <div className="mx-auto w-full max-w-sm px-4">
        <Card className="rounded-xl p-8 shadow-2xl">
          <div className="mb-6">
            <Button
              variant="ghost"
              onClick={() => navigate('/login')}
              className="mb-4 -ml-3 gap-2 px-3"
            >
              <ArrowLeft className="h-4 w-4" />
              Tilbake til innlogging
            </Button>

            <h1 className="mb-2 text-2xl font-bold text-foreground">Glemt passord?</h1>
            <p className="text-sm text-muted-foreground">
              Ingen problem! Skriv inn e-postadressen din, så sender vi deg en lenke for å tilbakestille passordet.
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {success && (
            <Alert className="mb-6 border-green-200 bg-green-50 text-green-800">
              <AlertDescription>
                Hvis e-postadressen er registrert, har vi sendt deg en lenke for å tilbakestille passordet. Sjekk innboksen din!
              </AlertDescription>
            </Alert>
          )}

          {!success && (
            <form onSubmit={handleSubmit} className="space-y-2">
              <Label htmlFor="email-input">E-post</Label>
              <Input
                id="email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                autoFocus
                className="mb-6"
              />

              <Button type="submit" size="lg" disabled={loading} className="w-full">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send tilbakestillingslenke'}
              </Button>
            </form>
          )}

          {success && (
            <Button
              variant="outline"
              size="lg"
              onClick={() => navigate('/login')}
              className="w-full"
            >
              Tilbake til innlogging
            </Button>
          )}
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
